import type { ContentTable } from '@/sanity.types'

type TableProps = {
  value: ContentTable
}

function CellContent({ content }: { content: string }) {
  return <span dangerouslySetInnerHTML={{ __html: content }} />
}

export function Table({ value }: TableProps) {
  const { table } = value

  if (!table?.rows || table.rows.length === 0) {
    return null
  }

  const firstRow = table.rows[0]
  const hasHeaderContent = firstRow.cells?.some((cell) => cell && cell.trim() !== '')
  const shouldRenderFirstRow = hasHeaderContent

  const headerRow = shouldRenderFirstRow ? firstRow : null
  const bodyRows = shouldRenderFirstRow ? table.rows.slice(1) : table.rows.slice(1)

  return (
    <div className="my-3xl lg:my-5xl">
      {/* Mobile: Card layout */}
      <div className="md:hidden space-y-md">
        {bodyRows.map((row) => (
          <div key={row._key} className="border-4 border-primary-200 rounded-md bg-neutral-50 p-md">
            {row.cells?.map((cell, index) => (
              <div key={index} className="mb-sm last:mb-0">
                {headerRow?.cells?.[index] && (
                  <div className="font-bold size-body-xs mb-2xs">
                    <CellContent content={headerRow.cells[index] || ''} />
                  </div>
                )}
                <div className="size-body-sm">
                  <CellContent content={cell || ''} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Desktop: Standard table */}
      <div className="hidden md:block overflow-x-auto">
        <div className="border-4 border-primary-200 rounded-md overflow-hidden">
          <table
            className="w-full bg-neutral-50 size-body-sm lg:size-body-md"
            style={{ borderSpacing: 0 }}
          >
            {headerRow && (
              <thead className="bg-primary-50">
                <tr>
                  {headerRow.cells?.map((cell, index) => (
                    <th
                      key={index}
                      className="border-r-4 border-b-4 border-primary-200 p-md md:p-2 font-bold text-left last:border-r-0"
                    >
                      <CellContent content={cell || ''} />
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={row._key}>
                  {row.cells?.map((cell, index) => {
                    const isLastRow = rowIndex === bodyRows.length - 1
                    const isFirstCell = index === 0
                    const isLastCell = index === (row.cells?.length || 0) - 1
                    return (
                      <td
                        key={index}
                        className={`border-r-4 border-primary-200 p-md md:p-2 last:border-r-0 ${!isLastRow ? 'border-b-4' : ''} ${isLastRow && isFirstCell ? 'rounded-bl' : ''} ${isLastRow && isLastCell ? 'rounded-br' : ''}`}
                      >
                        <CellContent content={cell || ''} />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
